import React, { useState } from "react";
import { useMutation } from "@apollo/client";

import { RotaButton } from "@teamrota/rota-design";
import { createSlotManager, StreamProvider } from "@teamrota/shiftstreamlib";

import Modal from "~/src/components/modal";
import {
  formatDateMonthTimeShort,
  formatHoursMinutes,
} from "~/src/utils/formatting";

import { IconDiv } from "~/src/components/payroll/style";
import IconLink from "~/src/containers/payroll/components/IconLink";
import Icon from "~/src/components/icon";
import Tabs from "~/src/containers/modals/components/tabs";
import { COLORS } from "~/src/styles/config.style";

import { cleanAddress, getFillText, getStateColor } from "../../../utils";

import { useEmailsForStream } from "../graphql/use-emails-for-stream";
import { useStream } from "../graphql/use-stream";

import { Provider } from "../../../components/provider";
import { shiftStreamClient } from "../../../graphql/client";
import { STREAM_RERUN } from "../graphql/mutation/stream-rerun";
import { STREAM_CLOSE } from "../graphql/mutation/stream-close";
import { STREAM_CANCEL } from "../graphql/mutation/stream-cancel";
import { STREAM_ALGO } from "../graphql/mutation/stream-algo";

import {
  ActionHelpText,
  ActionRow,
  Content,
  DetailsSurround,
  DetailsTable,
  Footer,
  Separator,
  Subtitle,
  Title,
} from "./styles";
import { CancelModal } from "../../../components/cancel";
import { ResultModal } from "../../../components/result";

interface StreamInfoModalProps {
  streamId: number | null;
  onClose: () => void;
}

const StreamInfoModal: React.FC<StreamInfoModalProps> = ({
  streamId,
  onClose,
}) => {
  if (!streamId) {
    return null;
  }

  const { stream } = useStream(streamId);

  if (!stream) {
    return null;
  }

  const modalTabs: Array<{ name: string; content: React.ReactNode }> = [
    {
      name: "Overview",
      content: <OverviewTab stream={stream} />,
    },
  ];

  if (stream.account?.provider === StreamProvider.EMAIL) {
    modalTabs.push({
      name: "Linked Emails",
      content: <EmailsTab stream={stream} />,
    });
  }

  modalTabs.push({
    name: "Slots",
    content: <SlotsTab stream={stream} />,
  });

  modalTabs.push({
    name: "Algorithm",
    content: <AlgorithmTab stream={stream} />,
  });

  return (
    <Modal
      isOpen={Boolean(stream)}
      isLoading={false}
      isOverlay={false}
      isLightOverlay={false}
      hasBtn={false}
      padding={0}
      background="white"
      onClose={onClose}
    >
      <Content>
        <Title>Stream #{stream.id}</Title>

        <Subtitle>
          <Provider account={stream.account} />
        </Subtitle>

        <Tabs
          className="stream-info-tabs"
          defaultTab="Overview"
          tabs={modalTabs as any}
        />
      </Content>
    </Modal>
  );
};

const OverviewTab = ({ stream }: { stream: any }) => {
  const id = parseInt(stream.id);

  const isReady = stream.state === "READY";
  const isAlgo = !stream.noAlgo;

  const [resultModal, setResultModal] = useState<{
    message: string;
    isError: boolean;
  } | null>(null);

  const [algo, { loading: isAlgoLoading }] = useMutation(STREAM_ALGO, {
    client: shiftStreamClient,
    refetchQueries: ["getStream", "getStreams"],
  });

  const handleAlgo = async (id: any, isAlgo: boolean) => {
    try {
      const result = await algo({ variables: { id, algo: isAlgo } });
      if (result?.data?.streamAlgo) {
        setResultModal(result.data.streamAlgo);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [cancel, { loading: isCancelLoading }] = useMutation(STREAM_CANCEL, {
    client: shiftStreamClient,
    refetchQueries: ["getStream", "getStreams"],
  });

  const handleCancel = async (id: any) => {
    try {
      const result = await cancel({ variables: { id } });
      if (result?.data?.streamCancel) {
        setResultModal(result.data.streamCancel);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [close, { loading: isCloseLoading }] = useMutation(STREAM_CLOSE, {
    client: shiftStreamClient,
    refetchQueries: ["getStream", "getStreams"],
  });

  const handleClose = async (id: any) => {
    try {
      const result = await close({ variables: { id } });
      if (result?.data?.streamClose) {
        setResultModal(result.data.streamClose);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [reRun, { loading: isReRunLoading }] = useMutation(STREAM_RERUN, {
    client: shiftStreamClient,
    refetchQueries: ["getStream", "getStreams"],
  });

  const handleReRun = async (id: any) => {
    try {
      const result = await reRun({ variables: { id } });
      if (result?.data?.streamReRun) {
        setResultModal(result.data.streamReRun);
      } else {
        setResultModal({
          message: `Something went wrong (${JSON.stringify(result)})`,
          isError: true,
        });
      }
    } catch (e) {
      setResultModal({
        message: `Something went wrong (${JSON.stringify(e)})`,
        isError: true,
      });
    }
  };

  const [confirmingCancelId, setConfirmingCancelId] = React.useState<
    any | null
  >(null);

  return (
    <DetailsSurround>
      <DetailsTable>
        <tr>
          <th>ID</th>
          <td>#{stream.id}</td>
        </tr>
        <tr>
          <th>Client</th>
          <td>{stream.shift?.sourceAccount?.accountName || "Unknown"}</td>
        </tr>
        <tr>
          <th>Venue</th>
          <td>{stream.shift?.venue?.name || "Unknown"}</td>
        </tr>
        <tr>
          <th>Role</th>
          <td>{stream.shift?.role?.name || "Unknown"}</td>
        </tr>
        <tr>
          <th>Time</th>
          <td>
            {stream.shift?.startTime
              ? formatDateMonthTimeShort(stream.shift.startTime)
              : "Unknown"}{" "}
            -{" "}
            {stream.shift?.endTime
              ? formatHoursMinutes(stream.shift.endTime)
              : "Unknown"}
          </td>
        </tr>
        <tr>
          <th>State</th>
          <td>
            <div
              style={{
                display: "inline-block",
                width: "auto",
                color: COLORS.WHITE,
                backgroundColor: getStateColor(stream),
                padding: "5px",
                borderRadius: "5px",
              }}
            >
              {stream.state}
            </div>
          </td>
        </tr>
        <tr>
          <th>Filled</th>
          <td>{getFillText(stream)}</td>
        </tr>
        <tr>
          <th>Shifts</th>
          <td>
            {stream.shiftId}
            {stream.adoptedShiftIds?.length > 0
              ? ", " + stream.adoptedShiftIds.join(", ")
              : ""}
          </td>
        </tr>
      </DetailsTable>

      <Footer>
        <ActionRow>
          <RotaButton
            onClick={() => isReady && isAlgo && handleReRun(id)}
            disabled={!isReady || !isAlgo}
            size="large"
            isLoading={isReRunLoading}
          >
            Re-Run Algo
          </RotaButton>
          <ActionHelpText>
            <b>Schedules the algorithm</b> to fill any slots with available
            candidates.
          </ActionHelpText>
        </ActionRow>

        <ActionRow>
          <RotaButton
            variant={isAlgo ? "contained" : "outlined"}
            disabled={!isReady}
            size="large"
            onClick={() => handleAlgo(id, !isAlgo)}
            isLoading={isAlgoLoading}
          >
            {isAlgo ? "Stop Algo" : "Resume Algo"}
          </RotaButton>
          <ActionHelpText>
            <b>Stops any further algorithm runs</b>, even if slots become
            available.
          </ActionHelpText>
        </ActionRow>

        <ActionRow>
          <RotaButton
            variant={"contained"}
            disabled={!isReady}
            size="large"
            onClick={() => handleClose(id)}
            isLoading={isCloseLoading}
          >
            Close Stream
          </RotaButton>

          <ActionHelpText>
            <b>Closes the stream</b>, removing any unfilled slots. <br />
            <strong>NOTE:</strong> For MVS streams, this may be overridden by a
            roster update.
          </ActionHelpText>
        </ActionRow>

        <ActionRow>
          <RotaButton
            variant={"contained"}
            disabled={!isReady}
            size="large"
            onClick={() => setConfirmingCancelId(id)}
            isLoading={isCancelLoading}
          >
            Cancel Stream
          </RotaButton>

          <ActionHelpText>
            <b>Cancel the stream</b> and any associated shifts and bookings.
            Candidates will be notified.
          </ActionHelpText>
        </ActionRow>
      </Footer>

      {resultModal && (
        <ResultModal
          message={resultModal.message ?? null}
          isError={resultModal.isError ?? false}
          onDismiss={() => setResultModal(null)}
        />
      )}

      {confirmingCancelId && (
        <CancelModal
          streams={[stream]}
          isLoading={isCancelLoading}
          onProceed={async () => {
            await handleCancel(confirmingCancelId);
            setConfirmingCancelId(null);
          }}
          onClose={() => setConfirmingCancelId(null)}
        />
      )}
    </DetailsSurround>
  );
};

const SlotsTab = ({ stream }: { stream: any }) => {
  const slotManager = createSlotManager(stream.account, stream);

  return (
    <DetailsSurround>
      <DetailsTable>
        <thead>
          <tr>
            <th style={{ width: "4vw" }}>ID</th>
            <th style={{ width: "4vw" }}>Unfilled</th>
            <th style={{ width: "4vw" }}>Filled</th>
            <th style={{ width: "4vw" }}>Total</th>
            <th>Candidates</th>
            <th>Restrictions</th>
          </tr>
        </thead>
        <tbody>
          {slotManager.getSlotsInfo().map((slot: any) => (
            <tr key={slot.sourceId}>
              <td>#{slot.sourceId}</td>
              <td>{slot.unfilled}</td>
              <td>{slot.filled}</td>
              <td>{slot.total}</td>
              <td>
                {slot.candidates.map((candidate: any) => (
                  <div key={candidate}>{candidate}</div>
                ))}
              </td>
              <td>{slot.gender}</td>
            </tr>
          ))}
        </tbody>
      </DetailsTable>
    </DetailsSurround>
  );
};

const EmailsTab = ({ stream }: { stream: any }) => {
  const { emails } = useEmailsForStream({ streamId: parseInt(stream.id) });

  return (
    <DetailsSurround>
      <DetailsTable>
        <thead>
          <tr>
            <th style={{ width: "4vw" }}>ID</th>
            <th>To/From</th>
            <th style={{ width: "6vw" }}>Received</th>
            <th style={{ width: "2.5vw" }}>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {emails.map((email: any) => (
            <tr key={email.id}>
              <td>#{email.id}</td>
              <td>
                {email.sender} ➡️ {cleanAddress(email.mailboxAddress)}
              </td>
              <td>
                {email.receivedAt
                  ? formatDateMonthTimeShort(email.receivedAt)
                  : "—"}
              </td>
              <td>
                <IconDiv>
                  <IconLink
                    name="MORE"
                    size={25}
                    isButton
                    color={Icon.colors.MAIN_ORANGE}
                    tooltip="Actions"
                    onClick={() => {}}
                  />
                </IconDiv>
              </td>
            </tr>
          ))}
        </tbody>
      </DetailsTable>
    </DetailsSurround>
  );
};

const AlgorithmTab = ({ stream }: { stream: any }) => {
  const [expanded, setExpanded] = React.useState<number | null>(null);

  const algoTableStyles: any = { width: "45vw", tableLayout: "fixed" };
  const algoCols = ["10%", "30%", "15%", "45%"];

  return (
    <DetailsSurround>
      <DetailsTable>
        <thead>
          <tr>
            <th style={{ width: "4vw" }}>ID</th>
            <th>Date/Time</th>
            <th>Skipped</th>
            <th>Places</th>
            <th>Matching</th>
            <th>Applied</th>
            <th>Proposed</th>
            <th style={{ width: "3.5vw" }}>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {stream.algoRuns.map((algoRun: any) => {
            return (
              <>
                <tr key={algoRun.id}>
                  <td>#{algoRun.id}</td>
                  <td>{formatDateMonthTimeShort(algoRun.createdAt)}</td>
                  <td>{algoRun.skipped ? "Yes" : "No"}</td>
                  <td>{algoRun.places}</td>
                  <td>{algoRun.matching}</td>
                  <td>{algoRun.applied}</td>
                  <td>{algoRun.proposed.length}</td>
                  <td>
                    <IconDiv>
                      <IconLink
                        name={"PLUS"}
                        size={25}
                        isButton
                        color={
                          expanded === algoRun.id
                            ? COLORS.WARM_GREY
                            : COLORS.MAIN_ORANGE
                        }
                        tooltip="Expand"
                        onClick={() =>
                          setExpanded(
                            expanded === algoRun.id ? null : algoRun.id,
                          )
                        }
                      />
                    </IconDiv>
                  </td>
                </tr>
                {expanded === algoRun.id && (
                  <tr>
                    <td colSpan={8}>
                      <DetailsTable
                        textAlign="left"
                        verticalAlign="top"
                        style={algoTableStyles}
                      >
                        <colgroup>
                          <col style={{ width: algoCols[0] }} />
                          <col style={{ width: algoCols[1] }} />
                          <col style={{ width: algoCols[2] }} />
                          <col style={{ width: algoCols[3] }} />
                        </colgroup>
                        <tbody>
                          <tr>
                            <th colSpan={4}>Proposed</th>
                          </tr>
                          {algoRun.proposed.length === 0 ? (
                            <tr>
                              <td colSpan={4}>No candidates proposed</td>
                            </tr>
                          ) : (
                            algoRun.proposed.map(
                              (proposed: any, index: number) => (
                                <tr key={`${algoRun.id}-proposed-${index}`}>
                                  <td>{index + 1}</td>
                                  <td>{proposed.score} points</td>

                                  <td>#{proposed.member.id}</td>

                                  <td>
                                    {proposed.member.firstName}{" "}
                                    {proposed.member.lastName}
                                  </td>
                                </tr>
                              ),
                            )
                          )}
                        </tbody>
                      </DetailsTable>

                      <Separator />

                      <DetailsTable
                        textAlign="left"
                        verticalAlign="top"
                        style={algoTableStyles}
                      >
                        <colgroup>
                          <col style={{ width: algoCols[0] }} />
                          <col style={{ width: algoCols[1] }} />
                          <col style={{ width: algoCols[2] }} />
                          <col style={{ width: algoCols[3] }} />
                        </colgroup>
                        <tbody>
                          <tr>
                            <th colSpan={4}>Rejections</th>
                          </tr>
                          {algoRun.rejections.length === 0 ? (
                            <tr>
                              <td colSpan={4}>No rejections recorded</td>
                            </tr>
                          ) : (
                            algoRun.rejections.map((rejection: any) => (
                              <>
                                {rejection.members.map(
                                  (member: any, index: number) => (
                                    <tr
                                      key={`${algoRun.id}-rejection-${rejection.reason}`}
                                    >
                                      {index === 0 && (
                                        <>
                                          <td
                                            rowSpan={rejection.members.length}
                                          >
                                            {index + 1}
                                          </td>
                                          <td
                                            rowSpan={rejection.members.length}
                                          >
                                            {rejection.reason}
                                          </td>
                                        </>
                                      )}

                                      <td>#{member.id}</td>

                                      <td>
                                        {member.firstName} {member.lastName}
                                      </td>
                                    </tr>
                                  ),
                                )}
                              </>
                            ))
                          )}
                        </tbody>
                      </DetailsTable>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </DetailsTable>
    </DetailsSurround>
  );
};

export default StreamInfoModal;
